/**
 * Скидочные карты и купоны.
 *
 * Владелец выдаёт карту постоянному гостю, задаёт процент и забывает
 * о ней: дальше кассир подносит карту к сканеру, и скидка встаёт сама.
 * Сюда возвращаются, когда карту потеряли или гость перестал ходить —
 * тогда её блокируют.
 *
 * Карта и купон — разные вещи, и это видно с первого взгляда: карта
 * работает всегда, купон сгорает после первой покупки.
 */
import { useState } from 'react';
import { useAskSheet, useNote } from './ui/ask';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const API = '/api/v1/cards';

type Card = {
  id: string; code: string; kind: 'PERCENT' | 'COUPON'; percent: number;
  holder: string | null; guestPhone: string | null;
  isActive: boolean; usedAt: string | null; validUntil: string | null;
  createdAt: string;
};

const date = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('ru-RU') : '—';

async function call<T>(path: string, token: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    method: opts.method ?? 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    const d = await res.json().catch(() => null) as { code?: string } | null;
    if (d?.code === 'CODE_TAKEN') throw new Error('Такой номер уже выдан другой карте');
    throw new Error('Не получилось. Попробуйте ещё раз');
  }
  return res.json() as Promise<T>;
}

export function CardsScreen({ token }: { token: string }) {
  const { ask, sheet } = useAskSheet();
  const { note, noteView } = useNote();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [fresh, setFresh] = useState<{ code: string; percent: number } | null>(null);

  const list = useQuery({
    queryKey: ['cards', q],
    queryFn: () => call<Card[]>(`?q=${encodeURIComponent(q)}`, token),
  });

  const setActive = useMutation({
    mutationFn: (v: { id: string; isActive: boolean }) =>
      call('/active', token, { method: 'POST', body: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cards'] }),
  });

  const rows = list.data ?? [];
  const live = rows.filter((c) => c.isActive && !c.usedAt).length;

  return (
    <section className="menu-cat">
      <header>
        <h2>Скидочные карты</h2>
        <button className="btn primary" onClick={() => setAdding(true)}>+ Карта</button>
      </header>

      <div className="cards">
        <div className="card">
          <span>Действующих</span>
          <b>{live}</b>
        </div>
        <div className="card">
          <span>Всего выдано</span>
          <b>{rows.length}</b>
        </div>
      </div>

      <input className="search" placeholder="Номер карты или имя гостя"
        value={q} onChange={(e) => setQ(e.target.value)} />

      {list.isPending ? <p className="hint">Загружаем…</p> : (
        <table className="grid">
          <thead>
            <tr>
              <th>Номер</th><th>Вид</th><th>Скидка</th>
              <th>У кого</th><th>Состояние</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className={c.isActive && !c.usedAt ? '' : 'off'}>
                <td className="card-num">{c.code}</td>
                {/* Карта и купон — разная судьба, и это читается сразу,
                    а не выводится из одного слова мелким кеглем */}
                <td>
                  <span className={`badge ${c.kind === 'COUPON' ? 'kind-coupon' : 'kind-card'}`}>
                    {c.kind === 'COUPON' ? 'Купон · одна покупка' : 'Карта · бессрочная'}
                  </span>
                </td>
                <td className="num">{c.percent}%</td>
                <td>
                  {c.holder ?? '—'}
                  {c.guestPhone && <div className="sub">{c.guestPhone}</div>}
                </td>
                <td>
                  {c.usedAt ? (
                    <>
                      <span className="badge st-suspended">использован</span>
                      <div className="sub">{date(c.usedAt)}</div>
                    </>
                  ) : !c.isActive ? (
                    <span className="badge st-suspended">заблокирована</span>
                  ) : c.validUntil ? (
                    <>
                      <span className="badge st-active">работает</span>
                      <div className="sub">до {date(c.validUntil)}</div>
                    </>
                  ) : (
                    <span className="badge st-active">работает</span>
                  )}
                </td>
                <td className="actions">
                  {/* Купон после использования не воскрешают: он сгорел,
                      и вернуть его значит выдать скидку дважды */}
                  {!c.usedAt && (
                    <button className="btn small ghost" onClick={() => void (async () => {
                      const next = !c.isActive;
                      const a = await ask({
                        title: next ? `Вернуть карту ${c.code} в работу` : `Заблокировать карту ${c.code}`,
                        sub: next
                          ? 'Карта снова начнёт давать скидку при считывании.'
                          : 'Нужно, если карту потеряли или гость перестал ходить.',
                        effects: [
                          ['Карта', c.code],
                          ['Кассир при считывании', next ? 'применит скидку' : 'увидит отказ'],
                          ['Вернуть', 'тем же нажатием'],
                        ],
                        danger: !next,
                        confirmLabel: next ? 'Вернуть в работу' : 'Заблокировать',
                      });
                      if (!a) return;
                      setActive.mutate({ id: c.id, isActive: next });
                      note(next ? `Карта ${c.code} снова в работе` : `Карта ${c.code} заблокирована`);
                    })()}>
                      {c.isActive ? 'Заблокировать' : 'Вернуть'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="note">
                {q ? 'Ничего не нашлось' : 'Карт пока нет. Выдайте первую постоянному гостю.'}
              </td></tr>
            )}
          </tbody>
        </table>
      )}

      {adding && (
        <NewCard token={token}
          onClose={() => setAdding(false)}
          onDone={(v) => {
            setAdding(false);
            setFresh(v);
            void qc.invalidateQueries({ queryKey: ['cards'] });
          }} />
      )}

      {fresh && (
        <div className="dialog">
          <div className="dialog-card">
            <h2>Карта выдана</h2>
            <div className="creds">
              <div><span>Номер</span><b className="card-num">{fresh.code}</b></div>
              <div><span>Скидка</span><b>{fresh.percent}%</b></div>
            </div>
            <p className="hint">
              Напишите номер на карте или наклейте штрихкод. Кассир поднесёт
              её к сканеру — скидка встанет в чек сама.
            </p>
            <div className="modal-actions">
              <button className="btn" onClick={() => {
                void navigator.clipboard?.writeText(fresh.code);
              }}>Скопировать номер</button>
              <button className="btn primary" onClick={() => setFresh(null)}>Готово</button>
            </div>
          </div>
        </div>
      )}
      {sheet}
      {noteView}
    </section>
  );
}

/** Выдача карты. Номер можно придумать или взять готовый со штрихкода. */
function NewCard({ token, onClose, onDone }: {
  token: string;
  onClose: () => void;
  onDone: (v: { code: string; percent: number }) => void;
}) {
  const [kind, setKind] = useState<'PERCENT' | 'COUPON'>('PERCENT');
  const [code, setCode] = useState('');
  const [percent, setPercent] = useState(10);
  const [holder, setHolder] = useState('');
  const [phone, setPhone] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [err, setErr] = useState('');

  const save = useMutation({
    mutationFn: () => call<{ id: string }>('', token, {
      method: 'POST',
      body: {
        code: code.trim().toUpperCase(), kind, percent,
        ...(holder.trim() ? { holder: holder.trim() } : {}),
        ...(phone.trim() ? { guestPhone: phone.trim() } : {}),
        ...(kind === 'COUPON' && validUntil ? { validUntil } : {}),
      },
    }),
    onSuccess: () => onDone({ code: code.trim().toUpperCase(), percent }),
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <div className="dialog">
      <div className="dialog-card">
        <h2>Новая карта</h2>

        <label>Что выдаём
          <select value={kind} onChange={(e) => setKind(e.target.value as 'COUPON')}>
            <option value="PERCENT">Карта — работает всегда</option>
            <option value="COUPON">Купон — сгорает после покупки</option>
          </select>
        </label>

        <label>Номер
          <input value={code} placeholder="1001 или код со штрихкода"
            autoCapitalize="characters"
            onChange={(e) => setCode(e.target.value)} />
          <i className="split">
            Считайте сканером готовую карту или придумайте номер сами
          </i>
        </label>

        <label>Скидка, %
          <input type="number" min={1} max={100} value={percent}
            onChange={(e) => setPercent(Math.min(100, Math.max(1, Number(e.target.value))))} />
        </label>

        <div className="row2">
          <label>Имя гостя
            <input value={holder} placeholder="Айгуль"
              onChange={(e) => setHolder(e.target.value)} />
          </label>
          <label>Телефон
            <input value={phone} placeholder="+7 701 …"
              onChange={(e) => setPhone(e.target.value)} />
            <i className="split">По нему карта свяжется с гостем в базе</i>
          </label>
        </div>

        {kind === 'COUPON' && (
          <label>Действует до
            <input type="date" value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)} />
          </label>
        )}

        {err && <p className="rb-warn">{err}</p>}

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Отмена</button>
          <button className="btn primary"
            disabled={code.trim().length < 2 || save.isPending}
            onClick={() => save.mutate()}>
            Выдать
          </button>
        </div>
      </div>
    </div>
  );
}
